import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Eye, EyeOff, X, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  'Mutual Fund Basics',
  'Types of Mutual Funds',
  'Investment Methods',
  'Risk and Return',
  'Taxation',
  'Investor Behaviour',
  'Retirement',
  'SIP',
  'SWP',
  'Market Updates',
];

const DIFFICULTY_LEVELS = ['beginner', 'intermediate', 'advanced'];

const emptyForm = {
  title: '',
  slug: '',
  category: CATEGORIES[0],
  difficulty_level: 'beginner',
  content: '',
  excerpt: '',
  published: false,
};

const slugify = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const ArticleManager = ({ onChange }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${API}/articles?published_only=false`);
      setArticles(res.data);
    } catch (e) {
      console.error('Failed to fetch articles', e);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (article) => {
    setEditingId(article.article_id);
    setForm({
      title: article.title,
      slug: article.slug,
      category: article.category,
      difficulty_level: article.difficulty_level,
      content: article.content,
      excerpt: article.excerpt,
      published: article.published,
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'title' && !editingId) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content || !form.excerpt) {
      toast.error('Please fill in title, excerpt and content');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || slugify(form.title) };
      if (editingId) {
        await axios.put(`${API}/articles/${editingId}`, payload);
        toast.success('Article updated');
      } else {
        await axios.post(`${API}/articles`, payload);
        toast.success('Article created');
      }
      closeForm();
      await fetchArticles();
      if (onChange) onChange();
    } catch (err) {
      console.error('Save failed', err);
      toast.error('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (article) => {
    if (!window.confirm(`Delete article "${article.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/articles/${article.article_id}`);
      toast.success('Article deleted');
      await fetchArticles();
      if (onChange) onChange();
    } catch (err) {
      console.error('Delete failed', err);
      toast.error('Failed to delete article');
    }
  };

  const togglePublish = async (article) => {
    try {
      await axios.put(`${API}/articles/${article.article_id}`, {
        title: article.title,
        slug: article.slug,
        category: article.category,
        difficulty_level: article.difficulty_level,
        content: article.content,
        excerpt: article.excerpt,
        published: !article.published,
      });
      toast.success(article.published ? 'Article unpublished' : 'Article published');
      await fetchArticles();
      if (onChange) onChange();
    } catch (err) {
      console.error('Toggle publish failed', err);
      toast.error('Failed to update article');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[#E5E5E5] p-8 text-center text-sm font-body text-[#737373]">
        Loading articles...
      </div>
    );
  }

  return (
    <div data-testid="article-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-heading font-medium text-[#A0451E]">Articles</h3>
          <p className="text-sm font-body text-[#737373]">Create, edit and publish investor-education articles.</p>
        </div>
        <button
          onClick={openCreate}
          className="btn-primary flex items-center gap-2"
          data-testid="new-article-btn"
        >
          <Plus className="w-4 h-4" />
          New Article
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E5E5] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#FDF8F5] border-b border-[#E5E5E5]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Title</th>
                <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Category</th>
                <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Level</th>
                <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Status</th>
                <th className="px-6 py-4 text-right text-sm font-body font-semibold text-[#A0451E]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-sm font-body text-[#737373]">
                    No articles yet. Click "New Article" to create your first one.
                  </td>
                </tr>
              ) : (
                articles.map((article, index) => (
                  <tr
                    key={article.article_id}
                    className="border-b border-[#E5E5E5] hover:bg-[#FDF8F5]"
                    data-testid={`article-row-${index}`}
                  >
                    <td className="px-6 py-4">
                      <p className="text-sm font-body font-semibold text-[#A0451E]">{article.title}</p>
                      <p className="text-xs font-body text-[#737373] line-clamp-1 max-w-md">{article.excerpt}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-body text-[#4A4A4A]">{article.category}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-body border border-[#E5E5E5] text-[#4A4A4A] capitalize">
                        {article.difficulty_level}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {article.published ? (
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-body border border-green-300 bg-green-100 text-green-800">
                          Published
                        </span>
                      ) : (
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-body border border-yellow-300 bg-yellow-100 text-yellow-800">
                          Draft
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => togglePublish(article)}
                          className="p-2 rounded-md hover:bg-[#FDF8F5] text-[#A0451E]"
                          title={article.published ? 'Unpublish' : 'Publish'}
                          data-testid={`article-toggle-publish-${index}`}
                        >
                          {article.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => openEdit(article)}
                          className="p-2 rounded-md hover:bg-[#FDF8F5] text-[#A0451E]"
                          title="Edit"
                          data-testid={`article-edit-${index}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(article)}
                          className="p-2 rounded-md hover:bg-red-50 text-red-600"
                          title="Delete"
                          data-testid={`article-delete-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {formOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          data-testid="article-form-modal"
        >
          <div className="bg-white rounded-2xl border border-[#E5E5E5] w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5] sticky top-0 bg-white">
              <h3 className="text-2xl font-heading font-medium text-[#A0451E]">
                {editingId ? 'Edit Article' : 'New Article'}
              </h3>
              <button
                onClick={closeForm}
                className="p-2 rounded-md hover:bg-[#FDF8F5] text-[#4A4A4A]"
                data-testid="article-form-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body"
                  data-testid="article-title-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">Slug *</label>
                  <input
                    type="text"
                    name="slug"
                    value={form.slug}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body"
                    data-testid="article-slug-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">Category *</label>
                  <select
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body"
                    data-testid="article-category-select"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">Difficulty Level *</label>
                <select
                  name="difficulty_level"
                  value={form.difficulty_level}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body capitalize"
                  data-testid="article-difficulty-select"
                >
                  {DIFFICULTY_LEVELS.map((d) => (
                    <option key={d} value={d} className="capitalize">{d}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">Excerpt *</label>
                <textarea
                  name="excerpt"
                  value={form.excerpt}
                  onChange={handleChange}
                  required
                  rows={2}
                  placeholder="A short summary shown in article listings"
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body"
                  data-testid="article-excerpt-input"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">Content *</label>
                <textarea
                  name="content"
                  value={form.content}
                  onChange={handleChange}
                  required
                  rows={12}
                  placeholder="Article body. You can use plain text or basic Markdown."
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body font-mono text-sm"
                  data-testid="article-content-input"
                />
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="published"
                  checked={form.published}
                  onChange={handleChange}
                  className="w-4 h-4"
                  data-testid="article-published-checkbox"
                />
                <span className="text-sm font-body text-[#4A4A4A]">
                  Publish immediately (make visible on Investor Education Centre)
                </span>
              </label>

              <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E5E5]">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-6 py-3 rounded-full font-semibold border border-[#E5E5E5] text-[#4A4A4A] hover:bg-[#FDF8F5] font-body"
                  data-testid="article-form-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                  data-testid="article-form-save"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : editingId ? 'Update Article' : 'Create Article'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleManager;
